import { QueryError } from "@/components/QueryError";
import { apiRequest } from "@/lib/queryClient";
import { useMemo, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Plus, MoreHorizontal, Trash2, LayoutList, LayoutGrid, Download, Upload, Star, Sparkles, FileBarChart } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { StarProspectPopover } from "@/components/financials/StarProspectPopover";
import { AddCompanyDrawer } from "@/components/add/AddCompanyDrawer";
import { ImportDialog } from "@/components/import/ImportDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { SourceControl } from "@/components/origination/SourceControl";
import { describeOrigination } from "@/components/origination/describeOrigination";
import { MvaTracker, mvaStatusClass } from "@/components/prospects/MvaTracker";
import { StartMvaDialog, type ProspectMva } from "@/components/prospects/StartMvaDialog";
import { useMvas } from "@/hooks/useMvas";
import { usePagedRows } from "@/hooks/usePagedRows";
import { AirtableGrid, Column, GridToolbar } from "@/components/data-grid";
import { ListPager } from "@/components/data-grid/ListPager";
import { ListShell } from "@/components/list/ListShell";
import { NameCell } from "@/components/list/NameCell";
import { ListTiles } from "@/components/list/ListTiles";
import { ChartChips, ALL_KEY } from "@/components/list/ChartChips";
import { prospectTiles, prospectStageChips } from "@/components/list/tiles/prospectTiles";
import { PROSPECT_STAGES, PROSPECT_PRIORITIES, PROSPECT_PRIORITY_LABELS, prospectStageIndex } from "@shared/prospectConstants";
import { ORIGINATION_SOURCE_TYPES, ORIGINATION_SOURCE_LABELS, type OriginationInput, type OriginationView } from "@shared/originationConstants";

// ─── Types ───────────────────────────────────────────

interface Prospect {
  id: string; name: string; industry: string | null; headquarters: string | null;
  stage: string | null; interestLevel: string | null; status: string | null;
  revenueRange: string | null; targetAmount: string | null; website: string | null;
  lastActivityAt: string | null; createdAt: string; enrichmentData?: any;
  financialsStarredAt: string | null; estimatedDealValue?: string | null; expectedFeePercentage?: string | null;
  origination: OriginationView | null;
  ownerUserId: string | null; ownerName: string | null;
  nextStep: { id: string; title: string; dueDate: string | null; assignee: string | null } | null;
  parked: boolean; revisitOn: string | null; pendingSuggestions: number;
  mva: ProspectMva | null;
}

const STAGES = [...PROSPECT_STAGES];
const PRIORITY_COLORS: Record<string, string> = { Hot: "bg-red-500", Warm: "bg-amber-500", Cool: "bg-blue-400" };
const STAGE_COLORS: Record<string, string> = {
  "Identified": "bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-400",
  "Contacted": "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  "In conversation": "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400",
  "MVA delivered": "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  "Engagement proposed": "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  "Nurture": "bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-400",
};

type View = "active" | "nurture" | "all";

function formatTimeAgo(date: string | null) {
  if (!date) return "—";
  const days = Math.floor((Date.now() - new Date(date).getTime()) / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
}
function shortDate(iso: string | null) {
  if (!iso) return null;
  return new Date(iso.length === 10 ? iso + "T00:00:00" : iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
const isPast = (iso: string | null) => !!iso && new Date(iso.length === 10 ? iso + "T23:59:59" : iso).getTime() < Date.now();

// ─── Page ────────────────────────────────────────────

export default function Prospects() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { canViewFinancials, user } = useAuth();
  const [search, setSearch] = useState("");
  const [view, setView] = useState<View>("active");
  const [mine, setMine] = useState(false);
  const [hot, setHot] = useState(false);
  const [noNext, setNoNext] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<string>("All");
  const [industryFilter, setIndustryFilter] = useState<string>("All");
  const [stageFilter, setStageFilter] = useState<string>(ALL_KEY);
  const [layout, setLayout] = useState<"list" | "kanban">("list");
  const [selectedRaw, setSelected] = useState<string[]>([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [setSourceOpen, setSetSourceOpen] = useState(false);
  const [sourceDraft, setSourceDraft] = useState<OriginationInput | null>(null);
  // The MVA tracker panel. Its open state is a per-viewer convenience, so it
  // lives in localStorage and the page works the same when that is blocked.
  const [mvaOpen, setMvaOpenState] = useState(() => { try { return localStorage.getItem("prospects.mvaTracker") === "1"; } catch { return false; } });
  const setMvaOpen = (v: boolean) => { setMvaOpenState(v); try { localStorage.setItem("prospects.mvaTracker", v ? "1" : "0"); } catch { /* storage blocked */ } };
  const [startMva, setStartMva] = useState<{ open: boolean; companyId: string | null }>({ open: false, companyId: null });
  const mvaPanelRef = useRef<HTMLDivElement>(null);
  const { data: openMvas = [] } = useMvas("open");
  const showMvas = () => { setMvaOpen(true); requestAnimationFrame(() => mvaPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })); };

  const { data: prospects = [], isLoading, error: prospectsError, refetch, isFetching } = useQuery<Prospect[]>({
    queryKey: ["/api/prospects"],
    queryFn: async () => { const r = await apiRequest("GET", "/api/prospects"); return r.json(); },
  });

  const invalidate = (ids: string[]) => {
    queryClient.invalidateQueries({ queryKey: ["/api/prospects"] });
    ids.forEach((cid) => queryClient.removeQueries({ queryKey: ["company", cid] }));
    setSelected([]);
  };

  const bulkArchive = useMutation({
    mutationFn: async (ids: string[]) => {
      const r = await fetch("/api/prospects/bulk-archive", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ids }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || "Archive failed");
      return r.json() as Promise<{ archived: number; skipped: number }>;
    },
    onSuccess: (data, ids) => {
      invalidate(ids); setConfirmDeleteOpen(false);
      toast.success(data.skipped > 0 ? `Deleted ${data.archived}, skipped ${data.skipped}` : `Deleted ${data.archived} prospect${data.archived === 1 ? "" : "s"}`, { description: "Restorable for 30 days from Settings." });
    },
    onError: (e: Error) => toast.error(e.message || "Could not delete"),
  });
  const bulkUpdate = useMutation({
    mutationFn: async ({ ids, updates }: { ids: string[]; updates: Record<string, unknown> }) => {
      const r = await fetch("/api/prospects/bulk-update", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ids, updates }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || "Update failed");
      return r.json();
    },
    onSuccess: (_d, v) => { invalidate(v.ids); toast.success("Updated"); },
    onError: (e: Error) => toast.error(e.message),
  });
  const bulkSetSource = useMutation({
    mutationFn: async ({ ids, origination }: { ids: string[]; origination: OriginationInput }) => {
      const r = await fetch("/api/prospects/bulk-set-source", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ids, origination }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || "Failed");
      return r.json();
    },
    onSuccess: (_d, v) => { invalidate(v.ids); setSetSourceOpen(false); setSourceDraft(null); toast.success("Source set"); },
    onError: (e: Error) => toast.error(e.message),
  });

  const industries = useMemo(() => [...new Set(prospects.map((p) => p.industry).filter(Boolean) as string[])].sort(), [prospects]);

  // Search, Source and Industry: the scope the tiles count (spec R3).
  const inScope = (p: Prospect) => {
    if (search) {
      const q = search.toLowerCase();
      const src = p.origination ? describeOrigination(p.origination).primary.toLowerCase() : "";
      if (!p.name.toLowerCase().includes(q) && !(p.industry ?? "").toLowerCase().includes(q) && !src.includes(q)) return false;
    }
    if (sourceFilter !== "All" && (p.origination?.sourceType ?? "__none__") !== sourceFilter) return false;
    if (industryFilter !== "All" && p.industry !== industryFilter) return false;
    return true;
  };
  const matches = (p: Prospect) => {
    if (!inScope(p)) return false;
    if (mine && p.ownerUserId !== user?.id) return false;
    if (hot && p.interestLevel !== "Hot") return false;
    if (noNext && p.nextStep) return false;
    return true;
  };
  const byStageThenActivity = (a: Prospect, b: Prospect) => {
    const s = prospectStageIndex(b.stage) - prospectStageIndex(a.stage);
    if (s !== 0) return s;
    return new Date(b.lastActivityAt || b.createdAt).getTime() - new Date(a.lastActivityAt || a.createdAt).getTime();
  };
  const filtered = prospects.filter(matches).sort(byStageThenActivity);
  const active = filtered.filter((p) => !p.parked);
  const parked = filtered.filter((p) => p.parked).sort((a, b) => (a.revisitOn ?? "9999").localeCompare(b.revisitOn ?? "9999"));

  const toTileRow = (p: Prospect) => ({ parked: !!p.parked, stage: p.stage ?? null, hasSource: !!p.origination, hasNextStep: !!p.nextStep });
  const tileRows = prospects.filter(inScope).map(toTileRow);
  const tiles = prospectTiles(tileRows, openMvas).map((t) =>
    t.key === "next" ? { ...t, active: noNext, onClick: () => setNoNext(!noNext) }
    : t.key === "mvas" ? { ...t, active: mvaOpen, onClick: () => (mvaOpen ? setMvaOpen(false) : showMvas()) }
    : t.key === "active" ? { ...t, active: view === "active", onClick: () => setView("active") }
    : t);

  // The stage chips count what the other filters leave, not their own choice.
  // A parked prospect has no pipeline stage, so the board and Nurture skip them.
  const stageChipsOn = layout === "list" && view !== "nurture";
  const stageChips = stageChipsOn
    ? <ChartChips ariaLabel="Stage" chips={prospectStageChips(filtered.map(toTileRow))} value={stageFilter} onChange={setStageFilter} allCount={view === "all" ? filtered.length : active.length} />
    : undefined;
  const byStageChip = (p: Prospect) => !stageChipsOn || stageFilter === ALL_KEY || (!p.parked && (p.stage || "Identified") === stageFilter);

  const listRows = (view === "nurture" ? parked : view === "all" ? [...active, ...parked] : active).filter(byStageChip);
  const pagedProspects = usePagedRows(listRows);
  // Selection scope is every row the current filters and view show, across all
  // pages. Ids hidden by a filter, the stage chip or a view switch stay in the
  // raw list but are never acted on.
  const listRowIds = useMemo(() => new Set(listRows.map((p) => p.id)), [listRows]);
  const selected = useMemo(() => selectedRaw.filter((id) => listRowIds.has(id)), [selectedRaw, listRowIds]);
  const onPageSelected = pagedProspects.rows.filter((p) => selectedRaw.includes(p.id)).length;
  const selectedLabel = selected.length > onPageSelected ? `${selected.length} selected across pages (${onPageSelected} on this page)` : `${selected.length} selected`;
  const selectedSet = useMemo(() => new Set<string | number>(selected), [selected]);

  const toggleSelect = (id: string) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const exportSelected = async () => {
    try {
      const r = await fetch("/api/prospects/export", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ids: selected.length > 0 ? selected : null }) });
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement("a"); a.href = url; a.download = "prospects.csv"; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success("Exported");
    } catch { toast.error("Export failed"); }
  };

  const chip = (on: boolean, label: string, onClick: () => void) => (
    <Button key={label} variant={on ? "default" : "outline"} size="sm" className="h-7 text-xs" onClick={onClick}>{label}</Button>
  );

  const prospectColumns: Column<Prospect>[] = [
    {
      id: "name",
      header: "Company",
      cell: (p) => {
        const sub = [p.industry, p.headquarters, p.revenueRange && `${p.revenueRange} rev`].filter(Boolean).join(" · ");
        return (
          <NameCell
            lead={<span className={`w-2 h-2 rounded-full shrink-0 mr-2 ${PRIORITY_COLORS[p.interestLevel || "Cool"]}`} title={PROSPECT_PRIORITY_LABELS[p.interestLevel || "Cool"]} />}
            name={p.name}
            nameClassName="font-semibold text-foreground"
            after={p.enrichmentData?.source ? <span className="w-2 h-2 rounded-full bg-green-500 shrink-0" title="Enriched" /> : undefined}
            sub={sub || undefined}
            title={sub ? `${p.name} · ${sub}` : p.name}
          />
        );
      },
    },
    {
      id: "source",
      header: "Source",
      cell: (p) => {
        const src = p.origination ? describeOrigination(p.origination) : null;
        const suggestions = `${p.pendingSuggestions} suggestion${p.pendingSuggestions === 1 ? "" : "s"}`;
        return src ? (
          <span className="flex items-center min-w-0 text-xs" title={[src.kind, src.primary, src.secondary].filter(Boolean).join(" · ")}>
            <span className="shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground mr-1.5">{src.kind}</span>
            <span className="min-w-0 truncate">
              {src.href ? <Link href={src.href} className="hover:underline" onClick={(e) => e.stopPropagation()}>{src.primary}</Link> : src.primary}
              {src.secondary && <span className="text-muted-foreground">{` · ${src.secondary}`}</span>}
            </span>
          </span>
        ) : p.pendingSuggestions > 0 ? (
          <Link href={`/prospects/${p.id}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400 hover:underline" title={suggestions}>
            <Sparkles className="w-3 h-3 shrink-0" /> <span className="truncate">{suggestions}</span>
          </Link>
        ) : <span className="text-xs text-muted-foreground italic">Not recorded</span>;
      },
      width: "w-[180px]",
    },
    {
      id: "stage",
      header: "Stage",
      cell: (p) => {
        const stage = p.parked ? "Nurture" : p.stage || "Identified";
        return (
          <span className="flex items-center gap-1.5 min-w-0">
            <Badge title={stage} className={`text-[11px] px-1.5 py-0 whitespace-nowrap shrink-0 ${STAGE_COLORS[stage] ?? STAGE_COLORS["Identified"]}`}>{stage}</Badge>
            {p.mva && (
              <button
                className={`min-w-0 truncate text-[11px] font-medium rounded px-1.5 py-0 ${mvaStatusClass(p.mva.status)}`}
                title={`MVA ${p.mva.status.toLowerCase()}${p.mva.dueOn ? `, due ${shortDate(p.mva.dueOn)}` : ""}`}
                onClick={(e) => { e.stopPropagation(); showMvas(); }}
              >
                {`MVA · ${p.mva.assigneeName ?? "Unassigned"}`}
              </button>
            )}
          </span>
        );
      },
      width: "w-[190px]",
    },
    {
      id: "owner",
      header: "Owner",
      cell: (p) => <span className="text-xs text-muted-foreground" title={p.ownerName ?? undefined}>{p.ownerName ?? "—"}</span>,
      width: "w-[100px]",
    },
    {
      id: "nextStep",
      header: "Next step",
      cell: (p) => {
        if (p.parked) {
          const text = `Revisit · ${p.revisitOn ? shortDate(p.revisitOn) : "no date"}${isPast(p.revisitOn) ? " · due" : ""}`;
          return <span title={text} className={`text-xs ${isPast(p.revisitOn) ? "text-amber-700 dark:text-amber-400 font-medium" : "text-muted-foreground"}`}>{text}</span>;
        }
        if (p.nextStep) {
          const due = shortDate(p.nextStep.dueDate) ?? "no date";
          return (
            <span className="flex items-center min-w-0 text-xs" title={`${p.nextStep.title} · ${due}${p.nextStep.assignee ? ` · ${p.nextStep.assignee}` : ""}`}>
              <span className="min-w-0 truncate">{p.nextStep.title}</span>
              <span className="shrink-0 whitespace-pre">{" · "}<b>{due}</b></span>
              {p.nextStep.assignee && <span className="shrink-0 text-muted-foreground whitespace-pre">{` · ${p.nextStep.assignee}`}</span>}
            </span>
          );
        }
        return <span className="text-xs text-amber-700/80 dark:text-amber-400/80">No next step</span>;
      },
      width: "w-[220px]",
    },
    {
      id: "lastActivity",
      header: "Last activity",
      cell: (p) => <span className="text-xs text-muted-foreground" title={formatTimeAgo(p.lastActivityAt || p.createdAt)}>{formatTimeAgo(p.lastActivityAt || p.createdAt)}</span>,
      width: "w-[90px]",
      className: "text-right",
      headerClassName: "text-right",
    },
    {
      id: "actions",
      header: "",
      cell: (p) => (
        <span className="flex items-center gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
          {canViewFinancials && (
            <StarProspectPopover prospect={{ id: p.id, name: p.name, estimatedDealValue: p.estimatedDealValue, expectedFeePercentage: p.expectedFeePercentage }} starred={!!p.financialsStarredAt}>
              <Star className={`w-4 h-4 ${p.financialsStarredAt ? "fill-current" : ""}`} />
            </StarProspectPopover>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-5 w-5 min-h-0 p-0" aria-label={`Actions for ${p.name}`}><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setLocation(`/prospects/${p.id}`)}>View</DropdownMenuItem>
              {p.mva
                ? <DropdownMenuItem onClick={showMvas}>MVA: {p.mva.status}</DropdownMenuItem>
                : <DropdownMenuItem onClick={() => setStartMva({ open: true, companyId: p.id })}>Start MVA…</DropdownMenuItem>}
              <DropdownMenuSeparator />
              {STAGES.map((s) => <DropdownMenuItem key={s} onClick={() => bulkUpdate.mutate({ ids: [p.id], updates: { stage: s } })}>→ {s}</DropdownMenuItem>)}
            </DropdownMenuContent>
          </DropdownMenu>
        </span>
      ),
      width: "w-[64px]",
      className: "py-0",
    },
  ];

  const hasFilters = !!search || mine || hot || noNext || sourceFilter !== "All" || industryFilter !== "All" || (stageChipsOn && stageFilter !== ALL_KEY);

  const sourceFilterConfig = {
    id: "source", label: "Source", value: sourceFilter, onChange: setSourceFilter, defaultValue: "All",
    options: [
      { value: "All", label: "All sources" },
      { value: "__none__", label: "Not recorded" },
      ...ORIGINATION_SOURCE_TYPES.map((t) => ({ value: t as string, label: ORIGINATION_SOURCE_LABELS[t] })),
    ],
  };
  const industryFilterConfig = {
    id: "industry", label: "Industry", value: industryFilter, onChange: setIndustryFilter, defaultValue: "All",
    options: [{ value: "All", label: "All industries" }, ...industries.map((i) => ({ value: i, label: i }))],
  };

  const emptyText = prospectsError ? "Prospects unavailable. Use Retry above."
    : prospects.length === 0 ? "No prospects yet. Add one, convert a website valuation, or promote an outreach reply."
    : "Nothing matches these filters.";

  const toolbarChips = (
    <div className="flex flex-wrap items-center gap-1.5 min-w-0">
      {stageChips}
      {stageChips && <span className="h-5 w-px bg-border shrink-0" aria-hidden />}
      {chip(view === "active", "Active", () => setView("active"))}
      {chip(view === "nurture", "Nurture", () => setView("nurture"))}
      {chip(view === "all", "All", () => setView("all"))}
      <span className="h-5 w-px bg-border shrink-0" aria-hidden />
      {chip(mine, "Mine", () => setMine(!mine))}
      {chip(hot, "Hot", () => setHot(!hot))}
      {chip(noNext, "No next step", () => setNoNext(!noNext))}
      {hasFilters && (
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setSearch(""); setMine(false); setHot(false); setNoNext(false); setSourceFilter("All"); setIndustryFilter("All"); setStageFilter(ALL_KEY); }}>Clear</Button>
      )}
    </div>
  );

  const toolbar = (
    <GridToolbar density="compact" searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search prospects..."
      chips={toolbarChips} filters={[sourceFilterConfig, industryFilterConfig]}
      actions={<>
        <Button variant={mvaOpen ? "default" : "outline"} size="sm" className="h-8 gap-1.5" onClick={() => (mvaOpen ? setMvaOpen(false) : showMvas())}>
          <FileBarChart className="w-4 h-4" /> MVAs in progress
          <span className={`ml-0.5 rounded-full px-1.5 text-[11px] font-semibold ${mvaOpen ? "bg-primary-foreground/20" : "bg-muted"}`}>{openMvas.length}</span>
        </Button>
        <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => setLayout(layout === "list" ? "kanban" : "list")}>
          {layout === "list" ? <LayoutGrid className="w-4 h-4" /> : <LayoutList className="w-4 h-4" />}{layout === "list" ? "Pipeline" : "List"}
        </Button>
        <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => setImportOpen(true)}><Upload className="w-4 h-4" /> Import</Button>
        <Button size="sm" className="h-8 gap-1.5" onClick={() => setAddOpen(true)}><Plus className="w-4 h-4" /> Add Prospect</Button>
      </>} />
  );

  const hasAbove = !!prospectsError || mvaOpen || selected.length > 0;
  const above = !hasAbove ? undefined : (
    <>
      {prospectsError && <QueryError error={prospectsError} onRetry={() => void refetch()} busy={isFetching} hasData={prospects.length > 0} noun="prospects" />}
      {mvaOpen && (
        <div ref={mvaPanelRef} className="scroll-mt-28 lg:max-h-[40vh] overflow-auto">
          <MvaTracker onStart={() => setStartMva({ open: true, companyId: null })} onClose={() => setMvaOpen(false)} />
        </div>
      )}
      {selected.length > 0 && (
        <div className="flex items-center gap-3 p-2 bg-primary/5 rounded-lg border border-primary/20">
          <span className="text-sm font-medium">{selectedLabel}</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="outline" size="sm" className="h-7 text-xs">Update stage</Button></DropdownMenuTrigger>
            <DropdownMenuContent>{STAGES.map((s) => <DropdownMenuItem key={s} onClick={() => bulkUpdate.mutate({ ids: selected, updates: { stage: s } })}>{s}</DropdownMenuItem>)}</DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="outline" size="sm" className="h-7 text-xs">Set priority</Button></DropdownMenuTrigger>
            <DropdownMenuContent>{PROSPECT_PRIORITIES.map((p) => <DropdownMenuItem key={p} onClick={() => bulkUpdate.mutate({ ids: selected, updates: { interestLevel: p } })}>{PROSPECT_PRIORITY_LABELS[p]}</DropdownMenuItem>)}</DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setSourceDraft(null); setSetSourceOpen(true); }}>Set source</Button>
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={exportSelected}><Download className="w-3 h-3" />Export</Button>
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-200 dark:text-red-400" onClick={() => setConfirmDeleteOpen(true)} disabled={bulkArchive.isPending}><Trash2 className="w-3 h-3" />Delete</Button>
          <Button variant="ghost" size="sm" className="h-7 text-xs ml-auto" onClick={() => setSelected([])}>Deselect</Button>
        </div>
      )}
    </>
  );

  const board = (!prospectsError || prospects.length > 0) && (
    <div className="grid grid-cols-6 gap-2 p-2 min-h-[400px] lg:min-h-0 lg:flex-1 lg:grid-rows-[minmax(0,1fr)]">
      {[...STAGES, "Nurture"].map((stage) => {
        const cards = stage === "Nurture" ? parked : active.filter((p) => (p.stage || "Identified") === stage);
        return (
          <div key={stage} className="flex flex-col min-h-0 rounded-lg bg-muted/30">
            <div className="shrink-0 flex items-center justify-between px-2.5 pt-2 pb-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground truncate">{stage}</span>
              <Badge variant="outline" className="text-[10px] h-5">{cards.length}</Badge>
            </div>
            <div className="lg:flex-1 lg:min-h-0 overflow-auto space-y-1.5 p-1.5">
              {cards.map((p) => {
                const src = p.origination ? describeOrigination(p.origination) : null;
                return (
                  <div key={p.id} className="bg-background rounded-lg border px-2 py-1.5 text-[13px] cursor-pointer hover:border-primary/50 transition-colors shadow-sm" onClick={() => setLocation(`/prospects/${p.id}`)}>
                    <div className="flex items-center gap-1.5 min-w-0 mb-0.5">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${PRIORITY_COLORS[p.interestLevel || "Cool"]}`} />
                      <span className="font-medium truncate">{p.name}</span>
                    </div>
                    <div className="text-xs text-muted-foreground space-y-0.5">
                      {src && <div className="truncate">{`${src.kind} · ${src.primary}`}</div>}
                      {p.ownerName && <div className="truncate">{p.ownerName}</div>}
                      {p.mva && <div className="truncate"><span className={`rounded px-1 ${mvaStatusClass(p.mva.status)}`}>{`MVA · ${p.mva.assigneeName ?? "Unassigned"}`}</span></div>}
                      {p.parked ? <div className="truncate">{`Revisit ${shortDate(p.revisitOn) ?? "no date"}`}</div> : p.nextStep ? <div className="truncate">{`${p.nextStep.title} · ${shortDate(p.nextStep.dueDate) ?? "no date"}`}</div> : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <ListShell
      title="Prospects"
      tiles={<ListTiles tiles={tiles} />}
      toolbar={toolbar}
      above={above}
      footer={layout === "list" ? (
        <ListPager density="compact" shown={pagedProspects.rows.length} total={pagedProspects.total} noun={["prospect", "prospects"]}
          page={pagedProspects.page} totalPages={pagedProspects.totalPages} pageSize={pagedProspects.pageSize} onPageSizeChange={pagedProspects.setPageSize}
          hasPrev={pagedProspects.hasPrev} hasNext={pagedProspects.hasNext} onPrev={pagedProspects.prev} onNext={pagedProspects.next} testIdPrefix="prospects" />
      ) : undefined}
      after={<>
        <ConfirmDialog
          open={confirmDeleteOpen}
          onOpenChange={(v) => !bulkArchive.isPending && setConfirmDeleteOpen(v)}
          title={`Delete ${selected.length} prospect${selected.length === 1 ? "" : "s"}?`}
          description={selected.length <= 5
            ? `This removes ${filtered.filter((p) => selected.includes(p.id)).map((p) => p.name).join(", ")} from your prospect list. They stay restorable for 30 days.`
            : `This removes ${selected.length} prospects, including ${filtered.filter((p) => selected.includes(p.id)).slice(0, 3).map((p) => p.name).join(", ")} and ${selected.length - 3} more. They stay restorable for 30 days.`}
          confirmLabel={bulkArchive.isPending ? "Deleting…" : "Delete"}
          variant="destructive"
          isLoading={bulkArchive.isPending}
          onConfirm={() => bulkArchive.mutate(selected)}
        />

        <Dialog open={setSourceOpen} onOpenChange={setSetSourceOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Set source for {selected.length} prospect{selected.length === 1 ? "" : "s"}</DialogTitle></DialogHeader>
            <SourceControl value={sourceDraft} onChange={setSourceDraft} currentUserId={user?.id} />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setSetSourceOpen(false)}>Cancel</Button>
              <Button disabled={!sourceDraft || bulkSetSource.isPending} onClick={() => sourceDraft && bulkSetSource.mutate({ ids: selected, origination: sourceDraft })}>Save</Button>
            </div>
          </DialogContent>
        </Dialog>

        <StartMvaDialog
          open={startMva.open}
          onOpenChange={(open) => setStartMva((s) => ({ ...s, open }))}
          prospects={prospects}
          initialCompanyId={startMva.companyId}
        />
        <AddCompanyDrawer open={addOpen} onOpenChange={setAddOpen} preselectedCategory="prospects" />
        <ImportDialog type="prospects" open={importOpen} onOpenChange={setImportOpen} />
      </>}
    >
      {layout === "list" ? (
        <AirtableGrid density="compact" scroll data={pagedProspects.rows} columns={prospectColumns} getRowId={(p) => p.id}
          onRowClick={(p) => setLocation(`/prospects/${p.id}`)} loading={isLoading}
          selectedRows={selectedSet} onSelectRow={(id) => toggleSelect(String(id))}
          onSelectAll={(checked) => {
            const pageIds = pagedProspects.rows.map((p) => p.id);
            setSelected((s) => checked ? [...s, ...pageIds.filter((id) => !s.includes(id))] : s.filter((id) => !pageIds.includes(id)));
          }}
          emptyState={<p className="text-sm text-muted-foreground text-center">{emptyText}</p>} />
      ) : board}
    </ListShell>
  );
}
